import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator'
import { useClient } from '../clients/use-client'
/**
 * Students list — the operator's global roster.
 *
 * GLOBAL roster per ADR-0052 D1 (revised): one table across every
 * client, with the client as a column and a filter — never a
 * navigation entrypoint. Search, lifecycle/attention filters, stable
 * sorting, and 25-row pagination all run server-side before the page is
 * sliced. Each row's Edit links to a
 * dedicated edit page (/admin/students/:userId/edit). English-only
 * internal tooling.
 *
 * Cohort membership appears here as a column and filter. Participation
 * changes belong to explicit enrollment operations; the roster's only bulk
 * write is recovery of failed invitation deliveries.
 */
import { useEffect, useState } from 'react'
import { Button, buttonVariants, Checkbox, Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { DateFilterInput } from '../common/date-filter-input'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { keysStaleAfterStudentChange, queryKeys } from '../api/query-keys'
import {
  ROW_LINK,
  ROW_LINK_ABOVE,
  TABLE,
  TABLE_WRAP,
  TABLE_HEAD_ROW,
  TABLE_ROW,
} from '../common/table'
import { Link, useSearchParams } from 'react-router-dom'
import { ClientPicker } from '../clients/client-picker'
import {
  ClassificationFilter,
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
  classificationParam,
} from '../common/list-page'
import { CohortPicker } from '../cohorts/cohort-picker'
import { InternalBadge } from '../cohorts/cohort-form-fields'
import { AdminApiError } from '../api/client.js'
import { adminStudentClient, messageForStudentError } from './api'
import type {
  AttentionReason,
  LifecycleStage,
  ListStudentsParams,
} from './types'
import { DismissibleAlert } from '../common/dismissible-alert'
import { InviteStateCell } from './invite-state-cell'
import {
  AttentionCell,
  LifecycleBadge,
} from './student-status'
import { ATTENTION_LABEL, ATTENTION_REASONS } from './student-attention'
import { LIFECYCLE_LABEL, LIFECYCLE_STAGES } from './student-lifecycle'

type LoadStatus = 'loading' | 'loaded' | 'error'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 250

function messageFor(err: unknown): string {
  if (err instanceof AdminApiError) return err.message
  return err instanceof Error ? err.message : 'Request failed'
}

/** ISO date/timestamp → YYYY-MM-DD. Locale-independent on purpose. */
function isoDate(value: string | null): string {
  return value ? value.slice(0, 10) : '—'
}

function programStartDate(value: string | null): string {
  return value === null ? 'Immediately' : isoDate(value)
}

function programEndDate(value: string | null): string {
  return value === null ? 'No scheduled end' : isoDate(value)
}

/** Inclusive because both boundary dates are usable program days. */
function programWindowLength(
  startsOn: string | null,
  endsOn: string | null,
): string | null {
  if (startsOn === null || endsOn === null) return null
  const start = Date.parse(`${isoDate(startsOn)}T00:00:00Z`)
  const end = Date.parse(`${isoDate(endsOn)}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null
  }
  const days = Math.round((end - start) / 86_400_000) + 1
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

function ProgramWindowCell({
  startsOn,
  endsOn,
}: {
  startsOn: string | null
  endsOn: string | null
}): JSX.Element {
  const length = programWindowLength(startsOn, endsOn)
  return (
    <>
      <span className="text-text-secondary">
        {programStartDate(startsOn)} → {programEndDate(endsOn)}
      </span>
      {length !== null && (
        <span className="block text-caption text-text-secondary mt-0.5">
          {length}
        </span>
      )}
    </>
  )
}

export function StudentsListPage(): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  // In the URL rather than component state so the client detail page can
  // deep-link into a filtered roster, and so a filtered view is
  // shareable and survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams()
  const clientId = searchParams.get('clientId') ?? ''
  const companyClient = useClient(clientId).data
  const cohortId = searchParams.get('cohortId') ?? ''
  // '' = both, 'true' = internal only, 'false' = customers only. Kept as the
  // raw URL string so "not set" and "set to false" stay distinguishable.
  const isInternalParam = searchParams.get('isInternal') ?? ''
  // '' = any, 'failed' = failed sends only (the reselect half of bulk
  // resend). Other operational states use the lifecycle/attention filters.
  const inviteStateParam = searchParams.get('inviteState') ?? ''
  // '' = any. Filtered server-side against the set-wise focused enrollment.
  const enrollmentStatusParam = searchParams.get('enrollmentStatus') ?? ''
  const startsOnOrAfterParam = searchParams.get('startsOnOrAfter') ?? ''
  const startsOnOrBeforeParam = searchParams.get('startsOnOrBefore') ?? ''
  const lifecycleParam = searchParams.get('lifecycleStage') ?? ''
  const needsAttentionParam = searchParams.get('needsAttention') ?? ''
  const attentionReasonParam = searchParams.get('attentionReason') ?? ''
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [resendResult, setResendResult] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const queryClient = useQueryClient()
  // Debounce the input into the server-side search term.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const listParams: ListStudentsParams = {
    search: search || undefined,
    clientId: clientId || undefined,
    cohortId: cohortId || undefined,
    isInternal: classificationParam(isInternalParam),
    inviteState: inviteStateParam === 'failed' ? ('failed' as const) : undefined,
    enrollmentStatus:
      enrollmentStatusParam === 'draft' ||
      enrollmentStatusParam === 'pending' ||
      enrollmentStatusParam === 'active' ||
      enrollmentStatusParam === 'suspended' ||
      enrollmentStatusParam === 'ended' ||
      enrollmentStatusParam === 'cancelled'
        ? enrollmentStatusParam
        : undefined,
    startsOnOrAfter: startsOnOrAfterParam || undefined,
    startsOnOrBefore: startsOnOrBeforeParam || undefined,
    lifecycleStage: LIFECYCLE_STAGES.includes(lifecycleParam as LifecycleStage)
      ? (lifecycleParam as LifecycleStage)
      : undefined,
    needsAttention:
      needsAttentionParam === 'true'
        ? true
        : needsAttentionParam === 'false'
          ? false
          : undefined,
    attentionReason: ATTENTION_REASONS.includes(
      attentionReasonParam as AttentionReason,
    )
      ? (attentionReasonParam as AttentionReason)
      : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const studentsQuery = useQuery({
    queryKey: queryKeys.studentList(listParams),
    queryFn: () => adminStudentClient.list(listParams),
    // Typing in the search mints a new key per keystroke; without this the
    // table unmounts into "Loading…" and FLASHES on every character. The
    // previous rows stay (dimmed below) until the next page arrives.
    placeholderData: keepPreviousData,
  })

  const students = studentsQuery.data?.students ?? []
  const total = studentsQuery.data?.total ?? 0
  const status: LoadStatus = studentsQuery.isPending
    ? 'loading'
    : studentsQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    studentsQuery.error !== null ? messageFor(studentsQuery.error) : null

  // Selection is keyed on ids that may leave the view when the filters
  // change, so a stale selection could act on a row the operator can no
  // longer see.
  useEffect(() => {
    setSelected(new Set())
    setResendResult(null)
  }, [
    search,
    clientId,
    cohortId,
    isInternalParam,
    inviteStateParam,
    enrollmentStatusParam,
    startsOnOrAfterParam,
    startsOnOrBeforeParam,
    lifecycleParam,
    needsAttentionParam,
    attentionReasonParam,
  ])

  // The enrollment-owned bulk recovery verb: resend for the whole
  // selection — the recovery for an outage mid-wave, paired with the
  // Invitation: Failed sends filter. Per-row outcomes server-side, so a
  // 200 can be a PARTIAL result; the honest line below names queued and
  // refused rows. Provider outcomes arrive later through the delivery ledger.
  const bulkResend = useMutation({
    mutationFn: () =>
      adminStudentClient.bulkResendInvites(clientId, {
        userIds: [...selected],
      }),
    onSuccess: (res) => {
      const parts: string[] = []
      if (res.queued > 0) {
        parts.push(
          `${res.queued} ${res.queued === 1 ? 'invitation' : 'invitations'} queued for delivery.`,
        )
      }
      if (res.queued === 0) {
        parts.push('No invitations were queued.')
      }
      if (res.skipped > 0) {
        parts.push(
          `${res.skipped} skipped (not eligible or rate-limited) — see the Invitation column.`,
        )
      }
      setResendResult(parts.join(' '))
      setSelected(
        new Set(
          res.results
            .filter((row) => row.outcome !== 'queued')
            .map((row) => row.userId),
        ),
      )
      for (const key of keysStaleAfterStudentChange(clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  const resending = bulkResend.isPending
  const resendError =
    bulkResend.error !== null ? messageForStudentError(bulkResend.error) : null

  function onBulkResend(): void {
    if (selected.size === 0 || !clientId) return
    setResendResult(null)
    bulkResend.mutate()
  }

  // Includes the SEARCH term, not just the rail: "Clear filters" that leaves
  // a search in place is the button not doing what it says.
  const hasFilters =
    search !== '' ||
    clientId !== '' ||
    cohortId !== '' ||
    isInternalParam !== '' ||
    inviteStateParam !== '' ||
    enrollmentStatusParam !== '' ||
    startsOnOrAfterParam !== '' ||
    startsOnOrBeforeParam !== '' ||
    lifecycleParam !== '' ||
    needsAttentionParam !== '' ||
    attentionReasonParam !== ''

  // Named and hoisted so the empty state can offer the SAME action as the rail
  // rather than a second implementation of it. Two copies of "clear" is how one
  // of them ends up forgetting the search term.
  function clearFilters(): void {
    setQuery('')
    setSearchParams(new URLSearchParams(), { replace: true })
    setPage(0)
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const start = safePage * PAGE_SIZE
  const pageRows = students

  return (
    <ListPage
      title="Students"
      count={status === 'loaded' ? total : undefined}
      action={
        <div className="flex gap-2">
          {/* Carries the active client filter into the batch form's first
              step; without a filter the picker starts unchosen.

              Same variant as Enroll one student deliberately: both CREATE
              enrollments (one vs a wave), and an outline button beside a filled twin
              read as its disabled state, not as a quieter sibling. */}
          <Link
            to={`/admin/students/batch${clientId !== '' ? `?clientId=${clientId}` : ''}`}
            className={buttonVariants({ variant: 'primary' }) + ' whitespace-nowrap'}
          >
            <Icon name="add" />
            Enroll several students
          </Link>
          <Link
            to="/admin/students/new"
            className={buttonVariants({ variant: 'primary' }) + ' whitespace-nowrap'}
          >
            <Icon name="add" />
            Enroll one student
          </Link>
        </div>
      }
      search={
        <SearchField
          id="student-search"
          label="Search students"
          placeholder="Search family name, given name or email…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setPage(0)
          }}
        />
      }
      // The RAIL's filters only — the search stays visible when the rail is
      // collapsed, so it does not need announcing on the badge.
      activeFilterCount={
        [
          clientId,
          cohortId,
          isInternalParam,
          inviteStateParam,
          enrollmentStatusParam,
          startsOnOrAfterParam,
          startsOnOrBeforeParam,
          lifecycleParam,
          needsAttentionParam,
          attentionReasonParam,
        ].filter((v) => v !== '').length
      }
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={clientId !== ''} label="Client" htmlFor="student-client-filter">
            <ClientPicker
              id="student-client-filter"
              label="Client"
              emptyLabel="All clients"
              value={clientId}
              showDetails={false}
              size="sm"
              onChange={(next) => {
                // Drop the param entirely when cleared, so the URL of an
                // unfiltered roster stays clean.
                if (next) searchParams.set('clientId', next)
                else searchParams.delete('clientId')
                // A cohort belongs to one client, so a cohort filter cannot
                // outlive a change of client.
                searchParams.delete('cohortId')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            />
          </FilterField>
          {/* Only once a client is chosen: cohorts belong to one client,
              so "all clients" has no cohort list to offer. */}
          {clientId !== '' && (
            <FilterField active={cohortId !== ''} label="Cohort" htmlFor="student-cohort-filter">
              <CohortPicker
                id="student-cohort-filter"
                label="Cohort"
                clientId={clientId}
                value={cohortId}
                emptyLabel="All cohorts"
                size="sm"
                onChange={(next) => {
                  if (next) searchParams.set('cohortId', next)
                  else searchParams.delete('cohortId')
                  setSearchParams(searchParams, { replace: true })
                  setPage(0)
                }}
              />
            </FilterField>
          )}
          <ClassificationFilter
            id="student-internal-filter"
            value={isInternalParam}
            help="Internal students are excluded from customer analytics. This is the filter that makes that carve-out auditable."
            onChange={(next) => {
              if (next) searchParams.set('isInternal', next)
              else searchParams.delete('isInternal')
              setSearchParams(searchParams, { replace: true })
              setPage(0)
            }}
          />
          {/* The reselect half of bulk resend: after an outage, "show me the
              failed rows" has to be expressible or recovery is a hunt.
              Other concerns belong to the lifecycle/attention filters. */}
          <FilterField active={listParams.inviteState !== undefined} label="Invitation" htmlFor="student-invite-filter">
            <Select
              value={inviteStateParam}
              onValueChange={(next) => {
                if (next) searchParams.set('inviteState', next)
                else searchParams.delete('inviteState')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            >
              <SelectTrigger id="student-invite-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any</SelectItem>
                <SelectItem value="failed">Failed sends</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={listParams.enrollmentStatus !== undefined}
            label="Enrollment status"
            htmlFor="student-enrollment-status-filter"
          >
            <Select
              value={enrollmentStatusParam}
              onValueChange={(next) => {
                if (next) searchParams.set('enrollmentStatus', next)
                else searchParams.delete('enrollmentStatus')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            >
              <SelectTrigger id="student-enrollment-status-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="ended">Ended</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={Boolean(listParams.startsOnOrAfter)}
            label="Enrollment starts from"
            htmlFor="student-starts-on-or-after-filter"
            help="Focused enrollment start date, inclusive. Periods without a scheduled start are excluded while this filter is set."
          >
            <DateFilterInput
              id="student-starts-on-or-after-filter"
              label="Enrollment starts from"
              size="sm"
              value={startsOnOrAfterParam}
              max={startsOnOrBeforeParam || undefined}
              onValueChange={(value) => {
                if (value) {
                  searchParams.set('startsOnOrAfter', value)
                } else {
                  searchParams.delete('startsOnOrAfter')
                }
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            />
          </FilterField>
          <FilterField active={Boolean(listParams.startsOnOrBefore)}
            label="Enrollment starts through"
            htmlFor="student-starts-on-or-before-filter"
            help="Focused enrollment start date, inclusive."
          >
            <DateFilterInput
              id="student-starts-on-or-before-filter"
              label="Enrollment starts through"
              size="sm"
              value={startsOnOrBeforeParam}
              min={startsOnOrAfterParam || undefined}
              onValueChange={(value) => {
                if (value) {
                  searchParams.set('startsOnOrBefore', value)
                } else {
                  searchParams.delete('startsOnOrBefore')
                }
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            />
          </FilterField>
          <FilterField active={listParams.lifecycleStage !== undefined} label="Lifecycle" htmlFor="student-lifecycle-filter">
            <Select
              value={lifecycleParam}
              onValueChange={(next) => {
                if (next) searchParams.set('lifecycleStage', next)
                else searchParams.delete('lifecycleStage')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            >
              <SelectTrigger id="student-lifecycle-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any</SelectItem>
                {LIFECYCLE_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {LIFECYCLE_LABEL[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={listParams.needsAttention !== undefined} label="Attention" htmlFor="student-attention-filter">
            <Select
              value={needsAttentionParam}
              onValueChange={(next) => {
                if (next) searchParams.set('needsAttention', next)
                else searchParams.delete('needsAttention')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            >
              <SelectTrigger id="student-attention-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any</SelectItem>
                <SelectItem value="true">Needs attention</SelectItem>
                <SelectItem value="false">Healthy</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={listParams.attentionReason !== undefined} label="Attention reason" htmlFor="student-attention-reason-filter">
            <Select
              value={attentionReasonParam}
              onValueChange={(next) => {
                if (next) searchParams.set('attentionReason', next)
                else searchParams.delete('attentionReason')
                setSearchParams(searchParams, { replace: true })
                setPage(0)
              }}
            >
              <SelectTrigger id="student-attention-reason-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any</SelectItem>
                {ATTENTION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {ATTENTION_LABEL[reason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        </>
      }
      pagination={
        status === 'loaded' && pageRows.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing {start + 1}–
                {Math.min(start + pageRows.length, total)} of {total}
              </>
            }
            page={safePage}
            pageCount={pageCount}
            previousDisabled={safePage === 0}
            nextDisabled={safePage >= pageCount - 1}
            onPrevious={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          />
        ) : undefined
      }
    >
      <>
        {/* A <section> rather than a <div>: an aria-label on a plain div
            creates no landmark, so the bar was unreachable by role for
            assistive tech (and for a role-based query). */}
        {selected.size > 0 && (
          <section
            aria-label="Bulk actions"
            className="mb-4 bg-surface-card rounded-token-card p-3 flex flex-col gap-2 text-body"
          >
            <div className="flex items-center gap-3">
              <span className="text-caption font-medium text-text-secondary">
                {selected.size} selected
              </span>
              <Button
                type="button"
                disabled={resending}
                onClick={() => setSelected(new Set())}
                variant="ghost"
                size="sm"
              >
                Clear selection
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                disabled={resending || clientId === ''}
                onClick={onBulkResend}
                variant="secondary"
                size="sm"
              >
                {resending ? 'Queueing…' : 'Resend activation email'}
              </Button>
            </div>
            <p className="text-caption text-text-secondary">
              Queues a fresh enrollment-owned activation delivery for every
              selected student. Previous links stop working; ineligible rows
              are skipped and reported. Choose one client first so the write
              stays within an explicit tenant scope.
            </p>
          </section>
        )}

        <DismissibleAlert
          message={resendError}
          onDismiss={() => bulkResend.reset()}
        />
        {resendResult !== null && (
          <p role="status" className="mb-4 text-body text-text-secondary">
            {resendResult}
          </p>
        )}

        {status === 'loading' && (
          <p className="text-text-secondary">Loading students…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load students</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}

        {status === 'loaded' && students.length === 0 && (
          <FilteredEmptyState
            hasFilters={hasFilters}
            noun="students"
            onClear={clearFilters}
            emptyHint="No students yet. Use Enroll one student to add one."
          />
        )}

        {/* Stale rows stay visible while the next result is in flight
            (keepPreviousData) — dimmed, so the refresh is honest without
            the table unmounting into a flash of "Loading…". */}
        {status === 'loaded' && students.length > 0 && (
          <div
            className={
              studentsQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
          <>
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  {/* Selection needs a client: a move is a write into one
                      tenant, and the composite FK makes a cross-client
                      membership unwritable anyway. */}
                  {clientId !== '' && (
                    <th className="p-3 w-8">
                      <Checkbox
                        aria-label="Select all on this page"
                        checked={
                          pageRows.length === 0
                            ? false
                            : pageRows.every((l) => selected.has(l.userId))
                              ? true
                              : pageRows.some((l) => selected.has(l.userId))
                                ? 'indeterminate'
                                : false
                        }
                        onCheckedChange={(checked) =>
                          setSelected((prev) => {
                            const next = new Set(prev)
                            for (const l of pageRows) {
                              if (checked === true) next.add(l.userId)
                              else next.delete(l.userId)
                            }
                            return next
                          })
                        }
                      />
                    </th>
                  )}
                  <FilterTh className="p-3 font-medium" label="Email" filterDescription={describeColumnFilters(search ? `Search: “${search}”` : undefined, isInternalParam === 'true' ? 'Internal only' : isInternalParam === 'false' ? 'Customers only' : undefined)} />
                  <FilterTh className="p-3 font-medium" label="Family name" filterDescription={search ? `Search: “${search}”` : undefined} />
                  <FilterTh className="p-3 font-medium" label="Given name" filterDescription={search ? `Search: “${search}”` : undefined} />
                  {companyClient?.clientEmployeeIdEnabled && <th className="p-3 font-medium">Employee ID</th>}
                  {companyClient?.clientDepartmentEnabled && <th className="p-3 font-medium">Department</th>}
                  <FilterTh className="p-3 font-medium" label="Client" filterDescription={clientId ? 'Selected client' : undefined} />
                  <FilterTh className="p-3 font-medium" label="Cohort" filterDescription={cohortId ? 'Selected cohort' : undefined} />
                  <th className="p-3 font-medium">Tier</th>
                  <FilterTh className="p-3 font-medium" label="Lifecycle" filterDescription={listParams.lifecycleStage ? `Lifecycle: ${listParams.lifecycleStage}` : undefined} />
                  <FilterTh className="p-3 font-medium" label="Attention" filterDescription={describeColumnFilters(listParams.needsAttention === undefined ? undefined : listParams.needsAttention ? 'Needs attention' : 'No attention needed', listParams.attentionReason && `Reason: ${listParams.attentionReason}`)} />
                  <FilterTh
                    className="p-3 font-medium"
                    title="Where each student is in the invitation lifecycle. The point of the column is the two states nobody would go looking for: a send that failed, and a send that was due and never happened."
                   label="Invitation" filterDescription={listParams.inviteState ? 'Failed invitations only' : undefined} />
                  <FilterTh
                    className="p-3 font-medium whitespace-nowrap"
                    title="Resolved start and end; the duration counts both boundary dates."
                   label="Access window" filterDescription={describeColumnFilters(listParams.enrollmentStatus && `Enrollment: ${listParams.enrollmentStatus}`, listParams.startsOnOrAfter && `Starts on or after ${listParams.startsOnOrAfter}`, listParams.startsOnOrBefore && `Starts on or before ${listParams.startsOnOrBefore}`)} />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => (
                  <tr
                    key={l.userId}
                    className={TABLE_ROW}
                  >
                    {clientId !== '' && (
                      <td className={`p-3 ${ROW_LINK_ABOVE}`}>
                        <Checkbox
                          aria-label={`Select ${l.email}`}
                          checked={selected.has(l.userId)}
                          onCheckedChange={(checked) =>
                            setSelected((prev) => {
                              const next = new Set(prev)
                              if (checked === true) next.add(l.userId)
                              else next.delete(l.userId)
                              return next
                            })
                          }
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <Link
                        to={`/admin/students/${l.userId}/edit`}
                        aria-label={`Edit ${l.email}`}
                        className={ROW_LINK}
                      >
                        {l.email}
                      </Link>
                      {l.isInternal && (
                        <span
                          className="mt-1 block"
                          title="Internal: excluded from customer analytics. Not an access flag."
                        >
                          <InternalBadge />
                        </span>
                      )}
                    </td>
                    <td className="p-3">{l.lastName ?? '—'}</td>
                    <td className="p-3">{l.firstName ?? '—'}</td>
                    {companyClient?.clientEmployeeIdEnabled && <td className="p-3">{l.clientEmployeeId ?? '—'}</td>}
                    {companyClient?.clientDepartmentEnabled && <td className="p-3">{l.clientDepartment ?? '—'}</td>}
                    <td className="p-3 text-text-secondary">{l.clientName}</td>
                    <td className="p-3 text-text-secondary">
                      {l.cohortName ?? '—'}
                    </td>
                    <td className="p-3">{l.tier}</td>
                    <td className="p-3">
                      <LifecycleBadge
                        stage={l.lifecycleStage}
                        className={ROW_LINK_ABOVE}
                      />
                    </td>
                    <td className="p-3">
                      <AttentionCell
                        reasons={l.attentionReasons}
                        className={ROW_LINK_ABOVE}
                      />
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <InviteStateCell student={l} />
                    </td>
                    <td className="p-3 whitespace-nowrap tabular-nums">
                      <ProgramWindowCell
                        startsOn={l.resolvedScheduledStartOn}
                        endsOn={l.programAccessEndsOn}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

          </>
          </div>
        )}
      </>
    </ListPage>
  )
}
